import { useModalContentToggler } from '@components'

import { Attachment, Button, DrawerContextProvider, useDetectScrollEndPosition } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { EInvoicesEmptyState } from '@views/receipts/elements/ReceiptsEmptyState/elements/EInvoicesEmptyState'
import { ScoutEmptyState } from '@views/receipts/elements/ReceiptsEmptyState/elements/ScoutEmptyState'
import { ScoutEmptyStateMode } from '@views/receipts/elements/ReceiptsEmptyState/elements/ScoutEmptyState/enums/scoutEmptyStateMode'

import { useIsAutoCegidIntegrationEnabled } from '../../../../../../hooks/useIsAutoCegidIntegrationEnabled'
import { EInvoicingSetupContextProvider } from '../../../../../eInvoicing/context/EInvoicingSetupContext'
import { AttachmentFilterType } from '../../../../enums/attachmentFiltertype'
import { AttachmentModalSteps } from '../../../../enums/attachmentModalSteps'
import { AttachmentList } from '../../../AttachmentList'
import { useChooseAttachmentModal } from '../../contexts/chooseAttachmentModalContext'
import { useAttachmentsForModal } from '../../hooks/useAttachmentsForModal'
import { ModalActions } from '../ModalActions'
import * as Styled from './styles'

export const ModalBody = (): ReactElement => {
  const [attachmentsVisible, setAttachmentsVisible] = useState<Attachment[]>([])
  const filesContainerRef = useRef<HTMLUListElement>(null)
  const { t } = useTranslation()
  const { attachments, isLoading, pagination } = useAttachmentsForModal()
  const { setActiveState } = useModalContentToggler()
  const { isAutoCegidIntegrationEnabled } = useIsAutoCegidIntegrationEnabled()

  const {
    filters: { searchQuery, sortDirection, sortProperty, type, page },
    handleFileImageClick,
    handleFileSelect,
    setFilters,
  } = useChooseAttachmentModal()

  const shouldShowAddNewTile =
    (type === AttachmentFilterType.All || type === AttachmentFilterType.Upload) && !isAutoCegidIntegrationEnabled
  const shouldShowScoutEmptyState = type === AttachmentFilterType.Scout && !attachments.length && !isLoading
  const shouldShowEInvoiceEmptyState = type === AttachmentFilterType.EDocument && !attachments.length && !isLoading
  const isLoadingList = isLoading || (attachmentsVisible.length === 0 && attachments.length > 0)

  // Set next page to fetch next page attachments
  const handleScrollEnd = useCallback(() => {
    if (pagination?.pageCount && page < pagination.pageCount) {
      setFilters({
        page: page + 1,
      })
    }
  }, [page, setFilters, pagination?.pageCount])

  const handleImageClick = useCallback(
    (attachment: Attachment) => {
      handleFileImageClick(attachment)
      setActiveState(AttachmentModalSteps.Preview)
    },
    [handleFileImageClick, setActiveState],
  )

  useEffect(() => {
    setAttachmentsVisible([])
  }, [type, searchQuery, sortDirection, sortProperty])

  useEffect(() => {
    if (attachments.length) {
      setAttachmentsVisible((prevAttachments) => {
        const isFreshFetch = page === 1
        return isFreshFetch ? attachments : [...prevAttachments, ...attachments]
      })
    }
  }, [attachments, page])

  useDetectScrollEndPosition({
    scrollableElement: filesContainerRef.current !== null ? filesContainerRef.current : undefined,
    onScrollEnd: handleScrollEnd,
    enabled: !isLoading,
    scrollEndOffset: 500,
  })

  return (
    <>
      <EInvoicingSetupContextProvider>
        <ModalActions />
        <Styled.ModalBodyWrapper ref={filesContainerRef}>
          {shouldShowEInvoiceEmptyState && <EInvoicesEmptyState />}
          {shouldShowScoutEmptyState && (
            <DrawerContextProvider>
              <ScoutEmptyState mode={ScoutEmptyStateMode.FilePicker} />
            </DrawerContextProvider>
          )}
          <AttachmentList isLoading={isLoadingList} shouldShowAddNewTile={shouldShowAddNewTile}>
            {attachmentsVisible?.map((attachment: Attachment) => {
              const handleSelectClick = () => handleFileSelect(attachment)

              return (
                <AttachmentList.Tile
                  attachment={attachment}
                  onImageClick={() => handleImageClick(attachment)}
                  footer={
                    <Button fullWidth onClick={handleSelectClick} variant="outlined" size="sm">
                      {t('choose_attachment_modal.select_attachment')}
                    </Button>
                  }
                  isSelectable={false}
                  key={attachment.id}
                  onFileSelectFromPreview={handleSelectClick}
                />
              )
            })}
          </AttachmentList>
        </Styled.ModalBodyWrapper>
      </EInvoicingSetupContextProvider>
    </>
  )
}
